"use client"

import { useState, useRef, useCallback, useEffect } from "react"
import { useSearchParams, useRouter } from "next/navigation"
import { buildRailItemsFromSession } from "@/lib/rail/rail-adapter"

import { CenteredChat } from "@/components/intelligence/centered-chat"
import { SignupModal } from "@/components/modals/signup-modal"
import { UpgradeModal } from "@/components/modals/upgrade-modal"
import { DeliverableReadyModal } from "@/components/modals/deliverable-ready-modal"
import { ReviewAIDraftModal } from "@/components/modals/review-ai-draft-modal"
import { ToolDrawer } from "@/components/layout/tool-drawer"
import { CollapsedRightRail } from "@/components/rail/collapsed-right-rail"
import { useReturnVisit } from "@/lib/hooks/use-return-visit"
import { useKnowledgeStore } from "@/lib/stores/knowledge-store"
import { useUserStore, SECTION_ACCESS, DEV_MODE } from "@/lib/stores/user-store"
import { useDeliverableStore } from "@/lib/stores/deliverable-store"
import { useFieldStatusStore } from "@/lib/stores/field-status-store"
import { useSidebarStore } from "@/lib/stores/sidebar-store"
import { useChatModeStore } from "@/lib/stores/chat-mode-store"
import type { BrainNodeSlug } from "@/lib/types/brain-nodes"

export const dynamic = "force-dynamic"

export default function AdvisorPage() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const initialQuestion = searchParams.get("q") || undefined
  const conversationId = searchParams.get("conversationId") || undefined

  const { isReturnVisit } = useReturnVisit()

  const [activeNode, setActiveNode] = useState<BrainNodeSlug | null>(null)
  const [showSignupModal, setShowSignupModal] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [signupTrigger, setSignupTrigger] = useState<
    "chat_limit" | "save_progress" | "section_complete" | "owner_profile_complete"
  >("save_progress")

  const [reviewingDraft, setReviewingDraft] = useState<{
    nodeSlug: BrainNodeSlug
    fieldPath: string
    fieldLabel: string
    content: string
    confidence: number
    sourceSections: string[]
  } | null>(null)

  const [messages, setMessages] = useState<any[]>([])
  const [pendingProposalsCount, setPendingProposalsCount] = useState(0)

  const containerRef = useRef<HTMLDivElement>(null)
  const { sections: nodes, getSectionBySlug: getNodeBySlug, updateSectionData: updateNodeData } = useKnowledgeStore()
  const { tier, userId, onboardingComplete } = useUserStore()
  const { toolDrawerOpen, openToolDrawer, closeToolDrawer } = useSidebarStore()
  const { showDeliverableModal, pendingDeliverable, closeDeliverableModal, viewDeliverable } = useDeliverableStore()
  const { getFieldStatus, markAsApproved, markAsEdited, markAsSkipped } = useFieldStatusStore()
  const { setCurrentFieldId, setMode } = useChatModeStore()

  const railItems = buildRailItemsFromSession()
  const railBadgeCount = railItems.length

  useEffect(() => {
    // applyAdvisorDefaults(onboardingComplete)
  }, [onboardingComplete])

  useEffect(() => {
    // TODO: Hook into StrategicFeed's proposal data when available
    // For now, simulate with 0 (will update when feed exposes proposal count)
    setPendingProposalsCount(0)
  }, [])

  useEffect(() => {
    setMode("advisory")
  }, [setMode])

  const handleSignupClick = () => {
    setSignupTrigger("owner_profile_complete")
    setShowSignupModal(true)
  }

  const handleUpgradeClick = () => {
    setShowUpgradeModal(true)
  }

  const handleReviewAIDraft = (nodeSlug: BrainNodeSlug, fieldPath: string) => {
    const node = getNodeBySlug(nodeSlug)
    const fieldStatus = getFieldStatus(nodeSlug, fieldPath)

    if (!node || !fieldStatus) return

    const nodeData = node.data as Record<string, unknown>
    const content = nodeData[fieldPath]

    const fieldLabel = fieldPath.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())

    setReviewingDraft({
      nodeSlug,
      fieldPath,
      fieldLabel,
      content: typeof content === "string" ? content : JSON.stringify(content),
      confidence: fieldStatus.aiConfidence || 0.85,
      sourceSections: fieldStatus.aiSourceSections || [],
    })
  }

  const handleApproveDraft = () => {
    if (!reviewingDraft) return
    markAsApproved(reviewingDraft.nodeSlug, reviewingDraft.fieldPath)
    setReviewingDraft(null)
  }

  const handleEditDraft = (editedContent: string) => {
    if (!reviewingDraft) return
    const previousValue = reviewingDraft.content
    updateNodeData(reviewingDraft.nodeSlug, { [reviewingDraft.fieldPath]: editedContent })
    markAsEdited(reviewingDraft.nodeSlug, reviewingDraft.fieldPath, previousValue)
    setReviewingDraft(null)
  }

  const handleRegenerateDraft = () => {
    console.log("Regenerating draft for:", reviewingDraft?.fieldPath)
  }

  const handleSkipDraft = () => {
    if (!reviewingDraft) return
    markAsSkipped(reviewingDraft.nodeSlug, reviewingDraft.fieldPath)
    setReviewingDraft(null)
  }

  const getNextNode = useCallback(() => {
    const accessibleNodes = DEV_MODE ? nodes : nodes.filter((n) => SECTION_ACCESS[tier].includes(n.slug))
    return accessibleNodes.find((n) => n.completionStatus !== "complete")
  }, [nodes, tier])

  const handleOpenNextNode = useCallback(() => {
    const nextNode = getNextNode()
    if (nextNode) {
      console.log("[v0] Editor functionality temporarily disabled in Stage B scaffold:", nextNode.slug)
    }
  }, [getNextNode])

  const handleFieldClick = useCallback(
    (fieldId: string) => {
      setCurrentFieldId(fieldId)
      setMode("advisory")
    },
    [setCurrentFieldId, setMode],
  )

  const handleViewReportFromModal = () => {
    if (pendingDeliverable) {
      console.log("[v0] Report viewing temporarily disabled in Stage B scaffold:", pendingDeliverable)
    }
  }

  return (
    <div className="flex h-full flex-col lg:flex-row overflow-hidden relative">
      {/* Center column - chat canvas with its own header */}
      <div ref={containerRef} className="flex-1 flex flex-col min-h-full" style={{ backgroundColor: "var(--surface)" }}>
        {/* Center-only header (64px height to match left sidebar header) */}
        <div className="hidden lg:flex h-16 items-center justify-between px-6 shrink-0">
          <div className="flex items-center gap-2">{/* Placeholder for future breadcrumb/context */}</div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col px-3 pb-3 relative">
          {isReturnVisit && <div className="px-4 pt-4">{/* Removed PulseBanner */}</div>}

          <CenteredChat
            activeSection={activeNode}
            onSectionChange={setActiveNode}
            onSignupClick={handleSignupClick}
            onEditSection={() => {}}
            onOpenNextSection={handleOpenNextNode}
            initialQuestion={initialQuestion}
            conversationId={conversationId}
            refreshSidebar={() => {}}
            onMessageChange={setMessages}
          />
        </div>
      </div>

      {!toolDrawerOpen && (
        <CollapsedRightRail
          onOpen={() => openToolDrawer("feed")}
          badgeCount={railBadgeCount}
          ariaLabel="Open Advisor Focus panel"
        />
      )}

      {/* Open state: full Actions panel */}
      <ToolDrawer conversationId={conversationId} pendingCount={pendingProposalsCount} />

      <SignupModal isOpen={showSignupModal} onClose={() => setShowSignupModal(false)} trigger={signupTrigger} />
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} trigger="section_access" />

      {showDeliverableModal && pendingDeliverable && (
        <DeliverableReadyModal
          sectionSlug={pendingDeliverable}
          onClose={closeDeliverableModal}
          onViewReport={handleViewReportFromModal}
          onUpgrade={handleUpgradeClick}
        />
      )}

      {reviewingDraft && (
        <ReviewAIDraftModal
          isOpen={true}
          onClose={() => setReviewingDraft(null)}
          fieldLabel={reviewingDraft.fieldLabel}
          draftContent={reviewingDraft.content}
          confidence={reviewingDraft.confidence}
          sourceSections={reviewingDraft.sourceSections}
          onApprove={handleApproveDraft}
          onEdit={handleEditDraft}
          onRegenerate={handleRegenerateDraft}
          onSkip={handleSkipDraft}
        />
      )}
    </div>
  )
}
